import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Calendar,
  BookOpenCheck,
  Link,
  ExternalLink,
  FileText,
  Video,
  Brain,
  X,
  Save,
  AlertCircle,
  Search,
  ChevronDown
} from 'lucide-react';

const HomeworkManagement = ({ 
  homework, 
  categories, 
  onRefresh, 
  showCreateForm, 
  setShowCreateForm, 
  editingItem, 
  setEditingItem 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    week_of: '',
    due_date: '',
    is_published: false,
    content_links: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Content data for linking
  const [videos, setVideos] = useState([]);
  const [resources, setResources] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);

  // Load content data when form opens
  useEffect(() => {
    if (showCreateForm) {
      loadContentData();
    }
  }, [showCreateForm]);

  const loadContentData = async () => {
    try {
      setContentLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Load videos, resources, and prompts in parallel
      const [videosResponse, resourcesResponse, promptsResponse] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL}/api/admin/videos`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL}/api/admin/resources`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (videosResponse.ok) {
        const videosData = await videosResponse.json();
        setVideos(videosData.data.videos || []);
      }

      if (resourcesResponse.ok) {
        const resourcesData = await resourcesResponse.json();
        setResources(resourcesData.data.resources || []);
      }

      if (promptsResponse.ok) {
        const promptsData = await promptsResponse.json();
        setPrompts(promptsData.data || []);
      }
    } catch (error) {
      console.error('Error loading content data:', error);
    } finally {
      setContentLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      title: '',
      description: '',
      week_of: '',
      due_date: '',
      is_published: false,
      content_links: []
    });
    setEditingItem(null);
    setShowCreateForm(true);
  };

  const handleEdit = (item) => {
    // Format dates for HTML date inputs (YYYY-MM-DD)
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    };
    
    setFormData({
      title: item.title,
      description: item.description,
      week_of: formatDateForInput(item.week_of),
      due_date: formatDateForInput(item.due_date),
      is_published: item.is_published,
      content_links: item.content_links || []
    });
    setEditingItem(item);
    setShowCreateForm(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      
      const token = localStorage.getItem('authToken');
      const url = editingItem 
        ? `${import.meta.env.VITE_API_URL}/api/homework/admin/${editingItem.id}`
        : `${import.meta.env.VITE_API_URL}/api/homework/admin/create`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save homework');
      }

      await onRefresh();
      setShowCreateForm(false);
      setEditingItem(null);
      setFormData({
        title: '',
        description: '',
        week_of: '',
        due_date: '',
        is_published: false,
        content_links: []
      });
    } catch (error) {
      setError(error.message);
      console.error('Error saving homework:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this homework assignment?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/homework/admin/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete homework');
      }

      await onRefresh();
    } catch (error) {
      setError(error.message);
      console.error('Error deleting homework:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/homework/admin/${id}/publish`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_published: !currentStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to toggle publish status');
      }

      await onRefresh();
    } catch (error) {
      setError(error.message);
      console.error('Error toggling publish status:', error);
    } finally {
      setLoading(false);
    }
  };

  const addContentLink = () => {
    const newLink = {
      link_type: 'video',
      content_id: null,
      external_url: '',
      link_text: '',
      description: '',
      display_order: formData.content_links.length
    };
    
    setFormData({
      ...formData,
      content_links: [
        ...formData.content_links,
        newLink
      ]
    });
  };

  const updateContentLink = (index, field, value) => {
    const updatedLinks = [...formData.content_links];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    setFormData({ ...formData, content_links: updatedLinks });
  };

  const removeContentLink = (index) => {
    const updatedLinks = formData.content_links.filter((_, i) => i !== index);
    setFormData({ ...formData, content_links: updatedLinks });
  };

  const getContentIcon = (linkType) => {
    switch (linkType) {
      case 'video': return <Video className="w-4 h-4" />;
      case 'resource': return <FileText className="w-4 h-4" />;
      case 'prompt': return <Brain className="w-4 h-4" />;
      case 'external': return <ExternalLink className="w-4 h-4" />;
      default: return <Link className="w-4 h-4" />;
    }
  };

  // Helper function to format dates without timezone issues
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'No date';
    
    // Handle date strings properly to avoid timezone issues
    // If it's a date-only string (YYYY-MM-DD), parse it as local date
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day) // month is 0-indexed
      return date.toLocaleDateString()
    }
    
    // For other date formats, use the original method
    return new Date(dateString).toLocaleDateString()
  };

  const getContentOptions = (linkType) => {
    switch (linkType) {
      case 'video':
        return videos.map(video => {
          const dateStr = formatDateForDisplay(video.training_date);
          return {
            value: video.id,
            label: `${video.title} (${dateStr})`,
            description: video.description?.substring(0, 100) + '...'
          };
        });
      case 'resource':
        return resources.map(resource => ({
          value: resource.id,
          label: resource.title,
          description: resource.description?.substring(0, 100) + '...'
        }));
      case 'prompt':
        return prompts.map(prompt => ({
          value: prompt.id,
          label: prompt.title,
          description: prompt.description?.substring(0, 100) + '...'
        }));
      default:
        return [];
    }
  };

  const getSelectedContentTitle = (linkType, contentId) => {
    if (!contentId) return 'Select content...';
    
    switch (linkType) {
      case 'video':
        const video = videos.find(v => v.id === contentId);
        if (video) {
          const dateStr = formatDateForDisplay(video.training_date);
          return `${video.title} (${dateStr})`;
        }
        return 'Select video...';
      case 'resource':
        const resource = resources.find(r => r.id === contentId);
        return resource ? resource.title : 'Select resource...';
      case 'prompt':
        const prompt = prompts.find(p => p.id === contentId);
        return prompt ? prompt.title : 'Select prompt...';
      default:
        return 'Select content...';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Homework Management</h2>
          <p className="text-gray-600">Create and manage weekly homework assignments</p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Homework
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Homework List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Homework Assignments</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Week Of
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Content Links
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {homework.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">
                      {item.description?.substring(0, 100)}...
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(item.week_of)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.is_published 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {item.is_published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.content_links?.length || 0} links
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleTogglePublish(item.id, item.is_published)}
                      className={`${
                        item.is_published 
                          ? 'text-yellow-600 hover:text-yellow-900' 
                          : 'text-green-600 hover:text-green-900'
                      }`}
                    >
                      {item.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingItem ? 'Edit Homework Assignment' : 'Create Homework Assignment'}
              </h3>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Homework assignment title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Week Of *
                  </label>
                  <input
                    type="date"
                    value={formData.week_of}
                    onChange={(e) => setFormData({ ...formData, week_of: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Detailed description of the homework assignment..."
                />
              </div>

              {/* Content Links */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Content Links
                  </label>
                  <button
                    type="button"
                    onClick={addContentLink}
                    className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Link
                  </button>
                </div>
                
                {contentLoading && (
                  <div className="text-center py-4 text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    Loading content...
                  </div>
                )}
                
                <div className="space-y-3">
                  {formData.content_links.map((link, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center">
                          {getContentIcon(link.link_type)}
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            Link {index + 1}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeContentLink(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Link Type
                          </label>
                          <select
                            value={link.link_type}
                            onChange={(e) => {
                              const newLinkType = e.target.value;
                              const updatedLinks = [...formData.content_links];
                              updatedLinks[index] = {
                                ...updatedLinks[index],
                                link_type: newLinkType,
                                content_id: null,
                                external_url: ''
                              };
                              setFormData({ ...formData, content_links: updatedLinks });
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="video">Video</option>
                            <option value="resource">Resource</option>
                            <option value="prompt">AI Prompt</option>
                            <option value="external">External Link</option>
                          </select>
                        </div>
                        
                        {link.link_type === 'external' ? (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              External URL
                            </label>
                            <input
                              type="url"
                              value={link.external_url}
                              onChange={(e) => updateContentLink(index, 'external_url', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="https://example.com"
                            />
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Select {link.link_type === 'video' ? 'Video' : link.link_type === 'resource' ? 'Resource' : 'AI Prompt'}
                            </label>
                            <select
                              value={link.content_id || ''}
                              onChange={(e) => updateContentLink(index, 'content_id', e.target.value || null)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Select {link.link_type === 'video' ? 'video' : link.link_type === 'resource' ? 'resource' : 'prompt'}...</option>
                              {getContentOptions(link.link_type).map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Link Text
                          </label>
                          <input
                            type="text"
                            value={link.link_text}
                            onChange={(e) => updateContentLink(index, 'link_text', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="e.g., Watch: Q4 Strategy Session"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Description
                          </label>
                          <input
                            type="text"
                            value={link.description}
                            onChange={(e) => updateContentLink(index, 'description', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="Optional description"
                          />
                        </div>
                      </div>
                      
                      {/* Show selected content info */}
                      {link.content_id && link.link_type !== 'external' && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                          <strong>Selected:</strong> {getSelectedContentTitle(link.link_type, link.content_id)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Publish Toggle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_published" className="ml-2 block text-sm text-gray-900">
                  Publish this homework assignment
                </label>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading || !formData.title || !formData.description || !formData.week_of}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingItem ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeworkManagement;
